import React, { useId } from 'react';
import { useActiveVisibilityBreakpoints } from '@de/hooks/useActiveVisibilityBreakpoints';
import {
  evaluateShowOn,
  VISIBILITY_BREAKPOINT_MEDIA,
  type VisibilityBreakpointToken,
} from './visibility';

function isVisibilityBreakpointToken(
  token: string,
): token is VisibilityBreakpointToken {
  return token in VISIBILITY_BREAKPOINT_MEDIA;
}

/**
 * SSR-safe CSS hide for `mode: 'css'` + `showOn`.
 *
 * Display is driven by pure `@media` rules (OR of listed tokens) so
 * server HTML and the client agree without waiting on JS. Shown state
 * uses `display: contents` so the wrapper does not become a flex/grid
 * item. The node stays mounted — including `<Trackable>` — which is
 * intentional: authors who need no phantom tracking should prefer
 * `mode: 'mount'`.
 *
 * `aria-hidden` mirrors the JS breakpoint match (belt-and-suspenders
 * for assistive tech when CSS hide alone is not enough).
 */
export function CssBreakpointVisibility({
  showOn,
  children,
}: {
  readonly showOn: readonly string[];
  readonly children: React.ReactNode;
}): React.ReactElement {
  const reactId = useId().replace(/:/g, '');
  const className = `de-vis-bp-${reactId}`;
  const activeBreakpoints = useActiveVisibilityBreakpoints();
  const matches = evaluateShowOn(showOn, activeBreakpoints);

  const mediaParts = showOn
    .filter(isVisibilityBreakpointToken)
    .map((token) => VISIBILITY_BREAKPOINT_MEDIA[token]);

  // Default hidden. Shown when any known token's media matches.
  // Unknown-only tokens → permanently hidden (fail closed for that bag).
  // `display: contents` when shown keeps flex/grid layout intact.
  const styleText =
    mediaParts.length === 0
      ? `.${className}{display:none!important}`
      : `.${className}{display:none!important}@media ${mediaParts.join(',')}{.${className}{display:contents!important}}`;

  return (
    <>
      <style>{styleText}</style>
      <div
        className={className}
        data-testid='de-visibility-css-hide'
        aria-hidden={!matches}
      >
        {children}
      </div>
    </>
  );
}
